
import { Component, Input, OnInit, ChangeDetectionStrategy, Output, EventEmitter, OnDestroy } from '@angular/core';
import { ReleaseMgmtService } from '../../../shared/services/releaseMgmtService';
import { FormBuilder, FormGroup, Validators, FormArray, AbstractControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { TableModule } from 'primeng/table';
/**
 * Interface for a service that can be mapped in the environment form.
 */
export interface Service {
  name: string;
  hasAPI: boolean;
  hasWeb: boolean;
  serviceId: string;
  serviceName: string;
}



@Component({
    standalone: true,
    selector: 'app-environment-form',
    imports: [CommonModule, ReactiveFormsModule, MatTooltipModule, TableModule, MatIconModule, MatCheckboxModule, MatFormFieldModule, MatInputModule, FormsModule, MatSelectModule, MatIconModule],
    templateUrl: './environment-form.component.html',
    styleUrl: './environment-form.component.scss'
})
export class EnvironmentFormComponent implements OnInit, OnDestroy {
  /** Mode of the form: 'add' or 'edit' */
  @Input() mode: 'add' | 'edit' = 'add';
  @Input() environment: any = null;
  @Output() cancel = new EventEmitter<void>();
  @Output() formSubmitted = new EventEmitter<any>();

  environmentFormGroup: FormGroup;
  availableServices: Service[] = [];
  private servicesSubscription: any;
  private environmentId: number | null = null;
  configEnvironmentNameInfoMsg = 'Allowed: Alphabets, Numbers and Hyphen. Maximum length: 75 characters.';

  constructor(
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    public toastr: ToastrService,
    private ReleaseMgmtService: ReleaseMgmtService
  ) {}

  initializeEnvironmentFormArray() {
  this.environmentFormGroup = this.formBuilder.group({
      environmentName: ['', [Validators.required, Validators.maxLength(75), Validators.pattern(/^[A-Za-z0-9_-]+$/)]], 
      workflowName: ['', [Validators.required, Validators.maxLength(500)]],
      environmentType: ['Production', Validators.required],
      isActive: [false],
      services: this.formBuilder.array([])
    });
  }

  /**
   * OnInit lifecycle hook: initializes services and pre-fills form if in edit mode.
   */
  ngOnInit() {
    if (!this.environmentFormGroup) {
      this.initializeEnvironmentFormArray();
    }
    if (this.mode === 'add') {
      // Fetch available services for add mode
      this.servicesSubscription = this.ReleaseMgmtService.apiGetActiveServices().subscribe((services: any[]) => {
        this.availableServices = services || [];
        // Now populate the services FormArray
        const servicesArray = this.environmentFormGroup.get('services') as FormArray;
        while (servicesArray.length) { servicesArray.removeAt(0); }
        this.initializeServiceFormArray();
        // Listen for changes in the services array to handle unmapping and active checkbox state
        this.environmentFormGroup.get('services')!.valueChanges.subscribe(() => {
          this.updateActiveCheckboxState();
        });
      });
    } else {
      // Only set availableServices from environment.services if not already set (for testability and correct prefill)
      if (!this.availableServices || this.availableServices.length === 0) {
        this.availableServices = (this.environment && Array.isArray(this.environment.services)) ? this.environment.services : [];
      }
      // Now populate the services FormArray
      const servicesArray = this.environmentFormGroup.get('services') as FormArray;
      while (servicesArray.length) { servicesArray.removeAt(0); }
      this.initializeServiceFormArray();
      // Always prefill after services array is initialized
      if (this.environment) {
        this.prefillFormWithEnvironment(this.environment);
      }
      // Listen for changes in the services array to handle unmapping and active checkbox state
      this.environmentFormGroup.get('services')!.valueChanges.subscribe(() => {
        this.updateActiveCheckboxState();
      });
    }
  }
  ngOnDestroy() {
    if (this.servicesSubscription) {
      this.servicesSubscription.unsubscribe();
    }
  }

  // omit special characters
  omit_special_char(event, isAlphaNumeric: boolean, addtionalCharAllowed: number[] = []) {
    // get char code
    const key = typeof event?.key === 'string' ? event.key : '';
    const k = key.codePointAt(0) ?? event?.charCode ?? event?.which ?? event?.keyCode;
    if (k === undefined) {
      return false;
    }

    if (isAlphaNumeric) {
      // allowed only Alphabets, numbers & hyphen
      return ((k > 64 && k < 91) || (k > 96 && k < 123) || k == 8 || addtionalCharAllowed.includes(k) || k == 45 || (k >= 48 && k <= 57));  }
    else {
      // allowed only number & hyphen
      return (k == 45 || (k >= 48 && k <= 57));  }
  }

  // omit special characters on paste
  on_paste_omit_special_char(event: ClipboardEvent, control, addtionalCharAllowed: number[] = []) {
    // local variables
    const clipboardData = event.clipboardData;
    let filteredText = '';

    // get text
    const pastedText = clipboardData.getData('text');
    let k;

    // loop to check for every char
    for (let i = 0; i < pastedText.length; i++) {
      k = pastedText.codePointAt(i) ?? 0;
      if ((k > 64 && k < 91) || (k > 96 && k < 123) || k == 8 || addtionalCharAllowed.includes(k) || k == 45 || (k >= 48 && k <= 57)) {
        filteredText += pastedText.charAt(i)
      }
    }
     window.document.execCommand('insertText', false, filteredText);

    // return false
    return false;
  }
  /**
   * Getter for the services FormArray.
   */
  get servicesFormArray(): FormArray<FormGroup> {
    return this.environmentFormGroup.get('services') as FormArray<FormGroup>;
  }

  /**
   * Initializes the services FormArray with controls for each available service.
   */
  initializeServiceFormArray() {
    this.availableServices.forEach((service: any, index: number) => {
      this.servicesFormArray.push(this.formBuilder.group({
        selected: [service.isSelected ?? false],
        apiUrl: [{ value: service.apiUrl, disabled: !service.isSelected } , [Validators.required, Validators.pattern(/^(https?:\/\/|www\.)((localhost)|([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(:\d+)?(\/.*)?$/)]],
        webUrl: [{ value: service.webUrl, disabled: !service.isSelected }, [Validators.required, Validators.pattern(/^(https?:\/\/|www\.)((localhost)|([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(:\d+)?(\/.*)?$/)]],
        serviceId: [service.serviceId || service.id || (service.serviceName ? service.serviceName : `service-${index}`)],
        serviceName: [service.serviceName || service.name || `Service ${index + 1}`],
        hasAPI: [service.hasAPI ?? false],
        hasWeb: [service.hasWeb ?? false]
      }));
    });
  }

  /**
   * Enables or disables the Active checkbox based on service selection.
   */
  private updateActiveCheckboxState() {
    const isAnyServiceSelected = this.servicesFormArray.controls.some(ctrl => ctrl.get('selected')!.value);
    const isActiveControl = this.environmentFormGroup.get('isActive');
    if (isAnyServiceSelected) {
      isActiveControl!.enable();
    } else {
      isActiveControl!.disable();
      isActiveControl!.setValue(false);
    }
  }

  /**
   * Prefills the form with the provided environment data (edit mode).
   * @param environment The environment object to prefill
   */
  private prefillFormWithEnvironment(environment: any) {
    this.environmentFormGroup.patchValue({
      environmentName: environment.environmentName,
      workflowName: environment.workflowName,
      environmentType: environment.environmentType,
      isActive: (environment.status == 'Active') ? true : false
    });

    this.environmentId = this.environment.id || null;

    if(this.mode === 'edit'){
      this.environmentFormGroup.get('environmentName')?.disable();
      this.environmentFormGroup.get('environmentType')?.disable();
    }else{
      this.environmentFormGroup.get('environmentName')?.enable();
      this.environmentFormGroup.get('environmentType')?.enable();
    }
    if (environment.services && Array.isArray(environment.services)) {
      environment.services.forEach((service: any, index: number) => {
        if (this.availableServices[index]) {
          this.servicesFormArray.at(index).patchValue({
            selected: service.isSelected ?? service.selected ?? false,
            apiUrl: service.apiUrl ?? '',
            webUrl: service.webUrl ?? '',
            serviceId: service.id ?? '',
            serviceName: service.serviceName ?? '',
            hasAPI: service.hasAPI ?? false,
            hasWeb: service.hasWeb ?? false
          });
        }
      });
    }
  }

 /**
   * Returns a validation error message for a given control and field type.
   * @param control The form control to check
   * @param fieldType The field type (for message)
   */
  getValidationError(control: AbstractControl | null, fieldType: string): string | null {
    if (!control || !control.touched || !control.errors) return null;
    if (control.errors['required']) return `${fieldType} is required`;
    if (control.errors['maxlength']) return `Max length exceeded`;
    if (control.errors['pattern']) return `Only alphabets and hyphens allowed`;
    return null;
  }

  /**
   * Returns a list of missing mandatory fields for validation.
   */
  getMissingMandatoryFields(): string[] {
  const missingFields: string[] = [];

  // Check basic form fields
  if (!this.environmentFormGroup.get('environmentName')?.value) {
    missingFields.push('Environment Name');
  }
  if (!this.environmentFormGroup.get('workflowName')?.value) {
    missingFields.push('Workflow Name');
  }

  // Check if any service is selected
  const isAnyServiceSelected = this.servicesFormArray.controls.some(ctrl => ctrl.get('selected')!.value);
  if (!isAnyServiceSelected) {
    missingFields.push('At least one service must be selected');
  }

  // Check service URLs & validation
  this.servicesFormArray.controls.forEach((ctrl: AbstractControl, index: number) => {
    if (ctrl.get('selected')!.value) {
      const service = this.availableServices[index];
      const serviceName = service.serviceName || service.name || `Service ${index + 1}`;
      
      // Check API URL if service has API
      if (service.hasAPI) {
        const apiUrlControl = ctrl.get('apiUrl');
        const apiUrlValue = apiUrlControl!.value?.trim();
        
        if (!apiUrlValue) {
          missingFields.push(`${serviceName} API URL is required`);
        } else if (apiUrlControl!.invalid) {
          missingFields.push(`${serviceName} API URL format is invalid`);
        }
      }
      
      // Check Web URL if service has Web
      if (service.hasWeb) {
        const webUrlControl = ctrl.get('webUrl');
        const webUrlValue = webUrlControl!.value?.trim();
        
        if (!webUrlValue) {
          missingFields.push(`${serviceName} Web URL is required`);
        } else if (webUrlControl!.invalid) {
          missingFields.push(`${serviceName} Web URL format is invalid`);
        }
      }
    }
  });

  return missingFields;
}

  /**
   * Returns true if the form is valid and all mandatory fields are filled.
   */
  isFormValid(): boolean {
    const missingFields = this.getMissingMandatoryFields();
    if (missingFields.length > 0) {
      return false;
    }
    return true;
  }

  /**
   * Handles the cancel action and emits the cancel event.
   */
  handleCancel() {
    this.environmentFormGroup.reset();
    this.environmentFormGroup.get('environmentType')!.setValue('Production');
    this.cancel.emit();
  }

  /* istanbul ignore next */
  handleFormSubmit() {
    let isValid = true;
    // Validate each selected service's URLs
    this.servicesFormArray.controls.forEach((ctrl: AbstractControl, index: number) => {
      if (ctrl.get('selected')!.value) {
        // TODO: Add URL regex validation here in future
        if (this.availableServices[index].hasAPI && !ctrl.get('apiUrl')!.value) {
          ctrl.get('apiUrl')!.setErrors({ required: true });
          ctrl.get('apiUrl')!.markAsTouched();
          isValid = false;
        }
        if (this.availableServices[index].hasWeb && !ctrl.get('webUrl')!.value) {
          ctrl.get('webUrl')!.setErrors({ required: true });
          ctrl.get('webUrl')!.markAsTouched();
          isValid = false;
        }
      }
    });
    if (this.environmentFormGroup.invalid || !isValid) {
      this.environmentFormGroup.markAllAsTouched();
    }
    // Emit id if present (edit mode)
    const formValue = this.environmentFormGroup.getRawValue();
    if (this.environmentId !== null) {
      formValue.id = this.environmentId;
    }
    if (!formValue.environmentName && this.environment) {
      formValue.environmentName = this.environment.environmentName;
    }

    // Ensure all required service fields are present
    if (Array.isArray(formValue.services)) {
      formValue.services = formValue.services.map((service: any, idx: number) => {
        const src = this.availableServices[idx] || {};
        return {
          ...service,
          serviceId: service.serviceId || (src as any)?.serviceId || (src as any)?.id || '',
          serviceName: service.serviceName || (src as any)?.serviceName || (src as any)?.name || `Service ${idx + 1}`,
          hasAPI: typeof service.hasAPI === 'boolean' ? service.hasAPI : (typeof (src as any)?.hasAPI === 'boolean' ? (src as any)?.hasAPI : false),
          hasWeb: typeof service.hasWeb === 'boolean' ? service.hasWeb : (typeof (src as any)?.hasWeb === 'boolean' ? (src as any)?.hasWeb : false)
        };
      });
    }
    this.formSubmitted.emit(formValue);
  }

    setDisable(index: number) {
      const isSelected = this.servicesFormArray.at(index).get('selected').value;
      const hasAPI = this.servicesFormArray.at(index).get('hasAPI').value;
      const hasWeb = this.servicesFormArray.at(index).get('hasWeb').value;
      if (isSelected) {
        if (hasAPI) {
          this.servicesFormArray.at(index).get('apiUrl').enable();
        }
        if (hasWeb) {
          this.servicesFormArray.at(index).get('webUrl').enable();
        }
      }
      else {
        if (hasAPI) {
          this.servicesFormArray.at(index).get('apiUrl').setValue('');
          this.servicesFormArray.at(index).get('apiUrl').disable();
        }
        if (hasWeb) {
          this.servicesFormArray.at(index).get('webUrl').setValue('');
          this.servicesFormArray.at(index).get('webUrl').disable();
        }
      }
    }
  
  onServiceCheckboxChange(event: MatCheckboxChange, index: number) {
  const checked = event.checked;
  if (typeof checked === 'undefined') {
    // Defensive: fallback to selected value from form
    const selected = this.servicesFormArray.at(index).get('selected')?.value;
    this.setDisable(index);
    return;
  }
  if (!checked && this.mode == 'edit') {
    const value1 = this.servicesFormArray.at(index).get('apiUrl').value;
    const value2 = this.servicesFormArray.at(index).get('webUrl').value;
    if (value1 !== '' || value2 !== '') {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: '400px',
        panelClass: 'custom-dialog-container'
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result === true) {
          this.setDisable(index);
          this.toastr.success('Service unmapped successfully.');
        } else {
          this.servicesFormArray.at(index).get('selected')!.setValue(true, { emitEvent: false });
        }
      });
      return;
    }
  }
  this.setDisable(index);
}

   onClear(){
    this.environmentFormGroup.reset();
    this.environmentFormGroup.get('environmentType')!.setValue('Production');
  }
}
